import { FC, useMemo, useState } from 'react';

import { Typography, Menu, Flex, theme, Image } from '@robinpowered/ui-kit';
import { RobinAvatar } from '../Avatar/RobinAvatar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Organization } from 'contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';

const HoverChevron = styled.div`
  opacity: 0;
  transition: opacity 0.1s ease;

  :before {
    content: unset !important;
  }

  :after {
    content: unset !important;
  }
`;

const MenuContainer = styled.div<{ collapsed: boolean }>`
  &:hover .hover-chevron {
    opacity: ${(props) => (props.collapsed ? 0 : 1)} !important;
  }
`;

const CustomExpandIcon = ({
  isSubMenu,
  collapsed,
}: {
  isSubMenu?: boolean;
  collapsed: boolean;
}) => {
  if (!isSubMenu || collapsed) return null;

  return (
    <HoverChevron className="ant-menu-submenu-arrow hover-chevron">
      <FontAwesomeIcon icon={faChevronRight} color="white" size="sm" />
    </HoverChevron>
  );
};

type OrgInfo = {
  id: string;
  name?: string | null | undefined;
  slug?: string | null | undefined;
  avatar?: string | null | undefined;
};

type MenuItem = {
  label: React.ReactNode;
  icon: React.ReactNode;
  key: string;
};

export function buildUserOrgMenu(
  orgs: OrgInfo[] | undefined,
  pathAfterOrgSlug: string
): MenuItem[] {
  if (!orgs) return [];

  return orgs.map((org) => {
    // Construct the target path: /newOrgSlug + pathAfterOrgSlug
    // If pathAfterOrgSlug is empty, just go to org root
    const targetPath = `/${org.slug}${pathAfterOrgSlug}`;

    return {
      label: (
        <Link to={targetPath} target="_self" rel="noopener noreferrer">
          {org.name}
        </Link>
      ),
      key: org.id,
      icon: (
        <RobinAvatar
          avatar={org.avatar}
          name={org.name}
          style={{ marginRight: 8 }}
        />
      ),
    };
  });
}

export const OrganizationDropdown: FC<{
  loading: boolean;
  error: boolean | undefined;
  userOrganizations: Organization[] | undefined;
  collapsed: boolean;
  currentOrg:
    | {
        id: string;
        name: string;
        slug: string;
        monochromeLogo: string;
        avatar: string;
      }
    | undefined;
  onOpenChange?: (isOpen: boolean) => void;
}> = ({
  loading,
  error,
  userOrganizations,
  currentOrg,
  collapsed,
  onOpenChange,
}) => {
  const { t } = useTranslation('globalNavigation');
  const { useToken } = theme;
  const { token } = useToken();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Extract the path after the org slug separately
  // This only recalculates when pathname or currentOrg slug changes
  const pathAfterOrgSlug = useMemo(() => {
    const currentPathname = location.pathname;
    const currentOrgSlug = currentOrg?.slug;

    if (currentOrgSlug && currentPathname.startsWith(`/${currentOrgSlug}/`)) {
      // Remove the leading "/orgSlug" part
      return currentPathname.slice(`/${currentOrgSlug}`.length);
    } else if (currentOrgSlug && currentPathname === `/${currentOrgSlug}`) {
      // If we're exactly at the org root, pathAfterOrgSlug stays empty
      return '';
    } else if (!currentOrgSlug && currentPathname.startsWith('/')) {
      // If no current org slug, try to extract path after first segment
      const segments = currentPathname.split('/').filter(Boolean);
      if (segments.length > 1) {
        // Remove first segment (assumed to be org slug) and reconstruct path
        return '/' + segments.slice(1).join('/');
      }
    }
    return '';
  }, [location.pathname, currentOrg?.slug]);

  const menuItems = useMemo(() => {
    return [
      {
        key: 'profile',
        label: (
          <Flex
            gap={4}
            align="center"
            style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
          >
            {currentOrg?.monochromeLogo && !collapsed ? (
              <Image
                src={currentOrg.monochromeLogo}
                alt={
                  currentOrg?.name || t('organization_dropdown.company_logo')
                }
                title={
                  currentOrg?.name || t('organization_dropdown.company_logo')
                }
                width={'80%'}
                preview={false}
                // This is a generic fallback image from ant's docs
                // eslint-disable-next-line max-len
                fallback="data:image/png;base64,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"
              />
            ) : (
              <>
                <RobinAvatar
                  avatar={currentOrg?.avatar}
                  name={currentOrg?.name}
                  style={{
                    flexShrink: 0,
                  }}
                />
                {!collapsed && (
                  <Typography.Title
                    style={{ color: 'white', fontSize: '1.5rem', margin: 0 }}
                    level={5}
                    ellipsis={{ tooltip: true }}
                  >
                    {currentOrg?.name}
                  </Typography.Title>
                )}
              </>
            )}
          </Flex>
        ),

        children: buildUserOrgMenu(userOrganizations, pathAfterOrgSlug),
      },
    ];
  }, [currentOrg, userOrganizations, collapsed, t, pathAfterOrgSlug]);

  const handleOpenChange = (openKeys: string[]) => {
    const isCurrentlyOpen = openKeys.includes('profile');
    setIsOpen(isCurrentlyOpen);
    onOpenChange?.(isCurrentlyOpen);
  };

  // @TODO: add an actual loading and error state
  if (loading || error || !userOrganizations?.length) {
    return null;
  }

  return (
    <MenuContainer collapsed={collapsed}>
      <Menu
        mode="vertical"
        style={{
          margin: `${token.marginSM}px 0`,
        }}
        items={menuItems}
        theme="dark"
        expandIcon={(props) => (
          <CustomExpandIcon {...props} collapsed={collapsed} />
        )}
        triggerSubMenuAction="click"
        openKeys={isOpen ? ['profile'] : []}
        onOpenChange={handleOpenChange}
      />
    </MenuContainer>
  );
};
